import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/axios'

export interface User {
  id: number
  email: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)
  const tokenCheckInterval = ref<NodeJS.Timeout | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  // Set auth token and user
  const setAuth = (authToken: string, userData: User) => {
    token.value = authToken
    user.value = userData
    localStorage.setItem('token', authToken)
    
    // Set api default header
    api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
  }

  // Login user
  const login = async (email: string, password: string) => {
    try {
      loading.value = true
      const response = await api.post('/api/auth/login', { email, password })
      const { token: authToken, user: userData } = response.data
      
      setAuth(authToken, userData)
      // Start periodic token check after successful login
      startTokenCheck()
      return { success: true }
    } catch (error: any) {
      console.error('Login error:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Недійсні облікові дані' 
      }
    } finally {
      loading.value = false
    }
  }

  // Logout user
  const logout = () => {
    stopTokenCheck()
    user.value = null
    token.value = null
    localStorage.removeItem('token')
    delete api.defaults.headers.common['Authorization']
    // Redirect to admin login page after logout
    window.location.href = '/admin'
  }

  // Verify token with backend
  const verifyToken = async () => {
    if (!token.value) return false
    
    try {
      const response = await api.get('/api/auth/verify')
      return response.status === 200
    } catch (error) {
      console.error('Token verification failed:', error)
      return false
    }
  }

  // Check if token is expired locally (basic check)
  const isTokenExpired = () => {
    if (!token.value) return true
    
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      return payload.exp < currentTime
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true
    }
  }

  // Handle token expiration
  const handleTokenExpiration = () => {
    console.log('Token expired, logging out user')
    stopTokenCheck()
    logout()
  }

  // Start periodic token check
  const startTokenCheck = () => {
    if (tokenCheckInterval.value) {
      clearInterval(tokenCheckInterval.value)
    }
    
    // Check token every 5 minutes
    tokenCheckInterval.value = setInterval(async () => {
      if (token.value && !isTokenExpired()) {
        const isValid = await verifyToken()
        if (!isValid) {
          handleTokenExpiration()
        }
      }
    }, 5 * 60 * 1000) // 5 minutes
  }

  // Stop periodic token check
  const stopTokenCheck = () => {
    if (tokenCheckInterval.value) {
      clearInterval(tokenCheckInterval.value)
      tokenCheckInterval.value = null
    }
  }

  // Initialize auth state from localStorage (basic initialization)
  const initAuth = async () => {
    if (token.value) {
      // Check if token is expired locally first
      if (isTokenExpired()) {
        console.log('Token is expired locally')
        handleTokenExpiration()
        return
      }
      
      // Set token in headers but don't verify with backend yet
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    }
  }

  // Initialize auth with verification (for admin pages)
  const initAuthWithVerification = async () => {
    if (!token.value) {
      return false
    }
    
    // Check if token is expired locally first
    if (isTokenExpired()) {
      console.log('Token is expired locally')
      handleTokenExpiration()
      return false
    }
    
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    
    // Verify token with backend
    const isValid = await verifyToken()
    if (!isValid) {
      console.log('Token verification failed with backend')
      handleTokenExpiration()
      return false
    } else {
      // Start periodic token check if token is valid
      startTokenCheck()
      return true
    }
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    initAuth,
    initAuthWithVerification,
    verifyToken,
    isTokenExpired,
    handleTokenExpiration,
    startTokenCheck,
    stopTokenCheck
  }
})
